import { Button, Tooltip, tokens, type IconName } from '@hotjar/hotsauce';
import styled from '@emotion/styled';
import { PlaceholderText } from './PlaceholderText/PlaceholderText';

export type XToolConnectorProps = {
  role?: string;
  ariaLabelledBy?: string;
  ariaDescribedBy?: string;
  title: React.ReactNode;
  description?: string;
  isLoading: boolean;
  helperText: string | undefined;
  lineColor?: string;
  actionButtonText: string;
  actionButtonIcon: IconName;
  actionButtonDisabled: boolean;
  errorMessage?: string;
  onActionButtonClick: () => void;
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: fit-content;
  height: fit-content;
  border: 1px solid ${tokens.color.ui_border};
  border-radius: ${tokens.radius.l};
  overflow: hidden;
  text-align: start;
  background: ${tokens.color.white};
`;

type BodyProps = {
  lineColor: string | undefined;
};

const Body = styled.div<BodyProps>`
  display: flex;
  flex-direction: column;
  gap: ${tokens.spacer.x1pt5};
  padding: ${tokens.spacer.x1pt5} ${tokens.spacer.x2pt5};

  ${({ lineColor }) =>
    lineColor === undefined
      ? ''
      : `
      padding-left: ${tokens.spacer.x2};
      border-left: ${tokens.spacer.x0pt5} solid ${lineColor};
    `}
`;

const Description = styled.span`
  ${tokens.text('caption')};
  color: ${tokens.color.text_light};
`;

const Title = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${tokens.spacer.x1};
  align-items: center;
  ${tokens.text('subtitle2')};
  color: ${tokens.color.text_default};
`;

const HelperText = styled.div`
  ${tokens.text('body2')};
  color: ${tokens.color.text_light};
  line-height: 20px;
  max-height: calc(20px * 3);
  overflow: hidden;
  text-overflow: ellipsis;

  /* This allows ellipsis overflow with multiple lines - https://caniuse.com/css-line-clamp  */
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  display: -webkit-box;
  /* */
`;

const Footer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: ${tokens.spacer.x6};
  border-top: 1px solid ${tokens.color.ui_border};
`;

const ActionButton = styled(Button)`
  ${tokens.text('subtitle2')};
  color: ${tokens.color.text_default};
`;

const TextContainer = styled.div`
  width: calc(33 * ${tokens.spacer.x1});
`;

const ErrorMessage = styled.span`
  color: ${tokens.color.text_error};
`;

export const XToolConnector = ({
  role = 'dialog',
  ariaLabelledBy,
  ariaDescribedBy,
  title,
  description,
  helperText,
  isLoading,
  lineColor,
  actionButtonText,
  actionButtonIcon,
  actionButtonDisabled,
  errorMessage,
  onActionButtonClick,
}: XToolConnectorProps): JSX.Element => {
  return (
    <Container role={role} aria-labelledby={ariaLabelledBy} aria-describedby={ariaDescribedBy}>
      <Body lineColor={lineColor}>
        {description !== undefined && <Description>{description}</Description>}

        <Title>{title}</Title>

        <TextContainer>
          {isLoading && <PlaceholderText />}
          {!isLoading && helperText !== undefined && (
            <Tooltip text={helperText}>
              <HelperText>{helperText}</HelperText>
            </Tooltip>
          )}
          {!isLoading && errorMessage !== undefined && <ErrorMessage>{errorMessage}</ErrorMessage>}
        </TextContainer>
      </Body>

      <Footer>
        <ActionButton
          isDisabled={actionButtonDisabled}
          isLoading={isLoading}
          onClick={onActionButtonClick}
          icon={actionButtonIcon}
          kind="ghost"
          size="s"
        >
          {actionButtonText}
        </ActionButton>
      </Footer>
    </Container>
  );
};
